<template>
  <div class="detail-attributes">
    <div class="detail-attributes__info">
      <div
        v-if="item.plmCode"
        class="detail-attributes__info-line"
      >
        <span class="title">PLM Code</span>
        <br>
        <span class="value">{{ item.plmCode }}</span>
        <ElementButton
          type="text"
          class="detail-attributes__copy-button"
          title="Copy PLM code to clipboard"
          @click="copyToClipboard"
        >
          <i class="el-icon-copy-document" />
        </ElementButton>
      </div>

      <div class="detail-attributes__info-line">
        <span class="title">
          {{ item.contributor ? 'Created by' : 'Created on' }}
        </span>
        <br>
        <span class="value">{{ createdInfoFormatted }}</span>
      </div>

      <div class="detail-attributes__info-line">
        <span class="title">Colorways Requested</span>
        <br>
        <span class="value detail-attributes__colorways-value">
          <template v-if="isLoadingColorwaysRequested">
            <StitchLoader
              size="mini"
              placement="start"
            />
          </template>
          <template v-else-if="isVendorUser">
            <template
              v-if="localColorwaysRequested && localColorwaysRequested.length"
            >
              <span
                v-for="cr in localColorwaysRequested"
                :key="cr"
                class="detail-attributes__colorway-tag"
              >
                {{ cr }}
              </span>
            </template>
            <span v-else>-</span>
          </template>
          <template v-else-if="!editableColorwaysRequested.edit">
            <template
              v-if="localColorwaysRequested && localColorwaysRequested.length"
            >
              <span
                v-for="cr in localColorwaysRequested"
                :key="cr"
                class="detail-attributes__colorway-tag"
              >
                {{ cr }}
              </span>
            </template>
            <span v-else>-</span>
            <i
              class="detail-attributes__button-actions el-icon-edit"
              @click="startEditingColorwaysRequested"
            />
          </template>
          <template v-else>
            <div class="detail-attributes__colorways-select-wrapper">
              <ElementSelect
                ref="colorwaysSelectRef"
                v-model="editableColorwaysRequested.colorwaysRequested"
                placeholder="Add colorway codes"
                filterable
                multiple
                allow-create
                default-first-option
                popper-class="colorways-select__hidden-dropdown"
                :popper-append-to-body="true"
                class="detail-attributes__colorways-select"
                :class="{ 'has-error': colorwaysTagError }"
              >
                <ElementOption
                  v-for="cr in localColorwaysRequested"
                  :key="cr"
                  :label="cr"
                  :value="cr"
                />
              </ElementSelect>
              <span
                v-if="colorwaysTagError"
                class="detail-attributes__colorways-error"
              >
                {{ colorwaysTagError }}
              </span>
            </div>

            <i
              class="detail-attributes__button-actions el-icon-check"
              @click="saveColorwaysRequested"
            />
            <i
              class="detail-attributes__button-actions el-icon-close"
              @click="cancelEditingColorwaysRequested"
            />
          </template>
        </span>
      </div>

      <div class="detail-attributes__info-line">
        <span class="title">Status</span>
        <br>
        <span class="value">
          <template v-if="isLoadingStatus">
            <StitchLoader
              size="mini"
              placement="start"
            />
          </template>
          <template v-else>
            <LibraryStatusSelector
              :item="item"
              :transitions="directStatusTransitions"
              @update-status="updateStatusWithoutConfirmation"
            />
          </template>
        </span>
      </div>

      <div class="detail-attributes__info-line">
        <span class="title">Submission Deadline</span>
        <br>
        <span class="value">
          <template v-if="!editableSubmissionDeadline.edit">
            <template
              v-if="
                isOpenStatus &&
                  item.submissionDeadline.days !== null &&
                  item.submissionDeadline.days <= 7
              "
            >
              <span
                :class="[
                  !item.submissionDeadline.is_passed ? 'close' : 'passed'
                ]"
              >
                {{ submissionDeadlineDate }}
              </span>
            </template>
            <span v-else>
              {{ submissionDeadlineDate }}
            </span>
            <i
              v-if="!isVendorUser"
              class="detail-attributes__button-actions el-icon-edit"
              @click="cancelEditingSubmissionDeadline"
            />
          </template>
          <template v-else>
            <ElementDatePicker
              v-model="editableSubmissionDeadline.deadline"
              type="date"
              placeholder="Submission Deadline"
              :picker-options="datePickerOptions"
              format="dd-MM-yyyy"
              value-format="yyyy-MM-dd"
              class="form-generator__content-item-date-picker"
            />
            <i
              class="detail-attributes__button-actions el-icon-check"
              @click="saveSubmissionDeadline"
            />
            <i
              class="detail-attributes__button-actions el-icon-close"
              @click="cancelEditingSubmissionDeadline"
            />
          </template>
        </span>
      </div>

      <div class="detail-attributes__info-line">
        <span class="title">Responsible Product</span>
        <br>
        <span class="value">
          <template v-if="isVendorUser">
            {{ item.responsibleProduct ? item.responsibleProduct : '-' }}
          </template>
          <template v-else-if="!editableResponsibleProduct.edit">
            {{ item.responsibleProduct }}
            <i
              class="detail-attributes__button-actions el-icon-edit"
              @click="cancelEditingResponsibleProduct"
            />
          </template>
          <template v-else>
            <ElementSelect
              v-model="editableResponsibleProduct.id"
              placeholder="Responsible Product"
              filterable
              clearable
            >
              <ElementOption
                v-for="rp in responsibleProductUsers"
                :key="rp.id"
                :label="rp.email"
                :value="rp.id"
              />
            </ElementSelect>
            <i
              class="detail-attributes__button-actions el-icon-refresh-left"
              title="Reset Responsible Product"
              @click="resetResponsibleProduct"
            />
            <i
              class="detail-attributes__button-actions el-icon-check"
              @click="saveResponsibleProduct"
            />
            <i
              class="detail-attributes__button-actions el-icon-close"
              @click="cancelEditingResponsibleProduct"
            />
          </template>
        </span>
      </div>

      <div class="detail-attributes__info-line">
        <span class="title">Digital Approver</span>
        <br>
        <span class="value">
          <template v-if="isVendorUser">
            {{ item.digitalApprover }}
          </template>
          <template v-else-if="!editableDigitalApprover.edit">
            {{ item.digitalApprover }}
            <i
              class="detail-attributes__button-actions el-icon-edit"
              @click="cancelEditingDigitalApprover"
            />
          </template>
          <template v-else>
            <ElementSelect
              v-model="editableDigitalApprover.id"
              placeholder="Digital Approver"
              filterable
              clearable
            >
              <ElementOption
                v-for="da in digitalApproverUsers"
                :key="da.id"
                :label="da.email"
                :value="da.id"
              />
            </ElementSelect>
            <i
              class="detail-attributes__button-actions el-icon-refresh-left"
              title="Reset Digital Approver"
              @click="resetDigitalApprover"
            />
            <i
              class="detail-attributes__button-actions el-icon-check"
              @click="saveDigitalApprover"
            />
            <i
              class="detail-attributes__button-actions el-icon-close"
              @click="cancelEditingDigitalApprover"
            />
          </template>
        </span>
      </div>

      <div
        v-if="!isVendorUser"
        class="detail-attributes__info-line"
      >
        <span class="title">Assigned Vendor Company</span>
        <br>
        <span class="value">
          <template v-if="isLoadingVendor">
            <StitchLoader
              size="mini"
              placement="start"
            />
          </template>
          <template v-else>
            <VendorSelector
              :vendors="vendors"
              :vendor-company-id="item.vendorCompanyId"
              @update-vendor="updateStyleVendor"
            />
          </template>
        </span>
      </div>

      <div
        v-if="!isVendorUser && isAdvancedUser"
        class="detail-attributes__info-line"
      >
        <span class="title">Quality Check</span>
        <br>
        <span class="value">
          <ElementSwitch
            v-model="styleQualityCheckStatus"
            :disabled="isLoadingQualityCheck"
            :class="{
              'detail-attributes__qc-neutral-state':
                styleQualityCheckStatus === null
            }"
            :active-color="styleQCActiveColor"
            :inactive-color="styleQCInactiveColor"
            @change="onQualityCheckChange"
          />
          <template v-if="!isLoadingQualityCheck">
            <ElementButton
              v-if="styleQualityCheckStatus !== null"
              class="detail-attributes__qc-reset"
              type="text"
              title="Reset Quality Check to Not Checked"
              @click="onQualityCheckReset"
            >
              <i class="el-icon-refresh-left" />
            </ElementButton>
            <br>
            <span>
              {{ qualityCheckTooltip }}
            </span>
            <ElementTooltip
              v-if="item.qualityCheckChangeLog"
              effect="dark"
              :content="`Changed by ${item.qualityCheckChangeLog.changed_by},
                  ${formatToLocaleDateShort(
                    item.qualityCheckChangeLog.changed_at
              )}`"
              placement="right-start"
              :open-delay="400"
              :enterable="false"
            >
              <i
                id="library-status--log-tooltip"
                class="el-icon-info"
              />
            </ElementTooltip>
          </template>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import FeatureFlags from '@/services/featureFlags'
import { mapActions, mapGetters } from 'vuex'
import { DataUtils } from '@/mixins/utils.js'
import {
  formatToLocaleDate,
  formatToLocaleDateShort,
  truncateText
} from '@/services/utils'
import { LIBRARY_TYPE } from '@/constants/libraryType'
import { ItemShape } from '@/types'
import LibraryStatusSelector from '@/components/library/LibraryStatusSelector.vue'
import VendorSelector from '@/components/library/VendorSelector.vue'
import { ICON_TYPE } from '@/constants/dropdownOptions'
import { ASSET_STATUS, CRITICAL_STATUSES } from '@/constants/assetStatuses'
import { USER_ROLE } from '@/constants/roleType'

const ALPHA_ONLY = /^[A-Za-z]$/
const COLORWAY_CODE = /^[A-Za-z]{3}$/
const MAX_LENGTH = 3

export default {
  name: 'DetailAttributes',

  components: {
    LibraryStatusSelector,
    VendorSelector
  },

  mixins: [DataUtils],

  props: {
    item: ItemShape.loose.isRequired
  },

  emits: ['edit-item', 'delete-item', 'asset-status-action'],

  data () {
    return {
      LIBRARY_TYPE,
      ICON_TYPE,
      ASSET_STATUS,
      USER_ROLE,
      watchState: false,
      isTogglingWatch: false,
      isLoadingStatus: false,
      isLoadingVendor: false,
      isLoadingQualityCheck: false,
      isLoadingColorwaysRequested: false,
      showStyleStatusActionForm: false,
      styleStatusActionDefaultFormData: null,
      styleStatusActionReasons: {},
      styleQCActiveColor: '#69db8d',
      styleQCInactiveColor: '$red',
      styleQualityCheckStatus: null,
      colorwaysTagError: null,
      disabledMessage:
        'As a vendor user, you are not allowed to perform this action.',
      editableResponsibleProduct: {
        id: null,
        email: null,
        edit: false
      },
      editableDigitalApprover: {
        id: null,
        email: null,
        edit: false
      },
      editableSubmissionDeadline: {
        deadline: null,
        edit: false
      },
      editableColorwaysRequested: {
        colorwaysRequested: [],
        edit: false
      },
      localColorwaysRequested: [],
      datePickerOptions: {
        /**
         * Disable any date before tomorrow.
         *
         * @param   {Date}    date - The date being evaluated by the picker.
         * @returns {boolean}      - `true` if the date should be disabled (i.e. is before tomorrow).
         */
        disabledDate (date) {
          const today = new Date()
          const tomorrow = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() + 1
          )

          return date < tomorrow
        }
      },
      showAllTags: false,
      maxVisibleTags: 7
    }
  },

  computed: {
    ...mapGetters(['getItemStatus', 'getStyleQualityStatus']),

    ...mapGetters({
      vendors: 'getVendors',
      user: 'getCognitoUserData',
      responsibleProductUsers: 'getResponsibleProductUsers',
      digitalApproverUsers: 'getDigitalApproverUsers'
    }),

    /**
     * @returns {boolean}
     */
    isOpenStatus () {
      return ![
        ASSET_STATUS.REVIEW.label,
        ASSET_STATUS.APPROVED.label,
        ASSET_STATUS.CANCELLED.label,
        ASSET_STATUS.PENDING.label,
        ASSET_STATUS.APPROVED_FOR_DAM.label
      ].includes(this.item.assetStatus)
    },

    /**
     * @returns {string}
     */
    submissionDeadlineDate () {
      return formatToLocaleDate(this.item.submissionDeadline.deadline)
    },

    /**
     * @returns {object}
     */
    itemDetail () {
      return this.getItemStatus()
    },

    /**
     * @returns {object}
     */
    qualityCheck () {
      if (this.item !== null && this.libraryType === LIBRARY_TYPE.STYLE) {
        return this.getStyleQualityStatus({ styleId: this.item.id })
      }

      return null
    },

    /**
     * @returns {string}
     */
    qualityCheckTooltip () {
      return this.styleQualityCheckStatus === null
        ? 'Not Set'
        : this.styleQualityCheckStatus
          ? 'Passed'
          : 'Needs Improvements'
    },

    /**
     * @returns {boolean}
     */
    isVendorUser () {
      return this.user.role === USER_ROLE.VENDOR.name
    },

    /**
     * @returns {boolean}
     */
    isAdvancedUser () {
      return FeatureFlags.isAdvancedUser()
    },

    /**
     * @returns {Array}
     */
    directStatusTransitions () {
      if (!this.itemDetail) {
        return []
      }

      return this.itemDetail.transitions.filter(
        transition =>
          !CRITICAL_STATUSES.includes(transition.value) &&
          transition.label !== this.item.assetStatus
      )
    },

    /**
     * @returns {string}
     */
    libraryType () {
      return this.item.libraryType
    },

    /**
     * @returns {string}
     */
    createdInfoFormatted () {
      if (this.item.contributor) {
        return `${this.item.contributor} on ${formatToLocaleDateShort(
          this.item.createdAt
        )}`
      }

      return formatToLocaleDateShort(this.item.createdAt)
    }
  },

  watch: {
    'item.watched': {
      immediate: true,
      /**
       * @param {boolean} val
       */
      handler (val) {
        this.watchState = val
      }
    },

    'item.colorwaysRequested': {
      immediate: true,
      /**
       * @param {object} value
       */
      handler (value) {
        this.localColorwaysRequested = Array.isArray(value) ? [...value] : []
      }
    },

    /**
     * Attach/detach the input listeners when the edit mode toggles
     *
     * @param {object} val
     */
    'editableColorwaysRequested.edit' (val) {
      if (val) {
        this.$nextTick().then(() => {
          this.attachColorwaysInputListener()
        })
      } else {
        this.detachColorwaysInputListener()
        this.colorwaysTagError = null
      }
    }
  },

  async created () {
    if (this.item !== null && this.libraryType === LIBRARY_TYPE.STYLE) {
      this.styleQualityCheckStatus = this.qualityCheck

      if (!this.isVendorUser) {
        await this.fetchAssignableVendorsByTeam({ teamId: this.item.groupId })
        await this.fetchResponsibleProductUsers({
          teamId: this.item.groupId
        })
        await this.fetchDigitalApproverUsers({
          teamId: this.item.groupId
        })

        if (this.item.submissionDeadline) {
          this.editableSubmissionDeadline.deadline =
            this.item.submissionDeadline.deadline
        }
      }

      if (this.item && typeof this.item.watched === 'boolean') {
        this.watchState = this.item.watched
      }
    }
  },

  beforeDestroy () {
    this.clearItemStatus({ libraryType: this.libraryType })
    this.detachColorwaysInputListener()
  },

  methods: {
    truncateText,

    formatToLocaleDateShort,

    ...mapActions([
      'fetchAssignableVendorsByTeam',
      'updateAssetStatus',
      'toggleWatchStyle',
      'clearItemStatus',
      'updateVendor',
      'updateQualityCheckStatus',
      'fetchResponsibleProductUsers',
      'fetchDigitalApproverUsers',
      'updateResponsibleProduct',
      'updateDigitalApprover',
      'updateSubmissionDeadline',
      'updateItem'
    ]),

    /**
     * @returns {object}
     */
    getColorwaysInput () {
      return (
        this.$refs.colorwaysSelectRef?.$el.querySelector('.el-select__input') ||
        this.$refs.colorwaysSelectRef?.$el.querySelector('input') ||
        null
      )
    },

    /**
     */
    attachColorwaysInputListener () {
      const input = this.getColorwaysInput()

      if (input) {
        input.addEventListener('keydown', this.handleColorwaysKeydown, true)
        input.addEventListener('input', this.handleColorwaysInput, true)
      }
    },

    /**
     */
    detachColorwaysInputListener () {
      const input = this.getColorwaysInput()

      if (input) {
        input.removeEventListener('keydown', this.handleColorwaysKeydown, true)
        input.removeEventListener('input', this.handleColorwaysInput, true)
      }
    },

    /**
     * Safety net for paste — scrubs non-alpha and enforces max length.
     *
     * @param {InputEvent} e
     */
    handleColorwaysInput (e) {
      const input = e.target
      const raw = input.value
      const cleaned = raw.replace(/[^A-Za-z]/g, '').slice(0, MAX_LENGTH)

      if (cleaned !== raw) {
        input.value = cleaned
        input.dispatchEvent(new Event('input', { bubbles: true }))
        this.colorwaysTagError =
          'Colorway codes must be exactly 3 letters (A–Z)'
      }
    },

    /**
     * @param {KeyboardEvent} e
     */
    handleColorwaysKeydown (e) {
      const input = e.target
      const currentVal = input.value

      if (
        [
          'Backspace',
          'Delete',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
          'Tab',
          'Escape'
        ].includes(e.key)
      ) {
        return
      }

      if (e.key === 'Enter') {
        const trimmed = currentVal.trim()

        if (!trimmed) {
          return
        }

        if (!COLORWAY_CODE.test(trimmed)) {
          e.preventDefault()
          e.stopPropagation()
          this.colorwaysTagError =
            'Colorway codes must be exactly 3 letters (A–Z)'

          return
        }

        const upper = trimmed.toUpperCase()
        const currentTags = Array.isArray(
          this.editableColorwaysRequested.colorwaysRequested
        )
          ? this.editableColorwaysRequested.colorwaysRequested.map(v =>
            (typeof v === 'string' ? v : String(v)).toUpperCase()
          )
          : []

        if (currentTags.includes(upper)) {
          e.preventDefault()
          e.stopPropagation()
          this.colorwaysTagError = `"${upper}" is already added`
          this.highlightColorwaysDuplicate(upper)

          return
        }

        e.preventDefault()
        e.stopPropagation()

        this.editableColorwaysRequested.colorwaysRequested = [
          ...new Set([...currentTags, upper])
        ]

        input.value = ''
        input.dispatchEvent(new Event('input', { bubbles: true }))

        this.colorwaysTagError = null

        return
      }

      if (!ALPHA_ONLY.test(e.key)) {
        e.preventDefault()
        e.stopPropagation()
        this.colorwaysTagError =
          'Colorway codes must be exactly 3 letters (A–Z)'

        return
      }

      if (currentVal.length >= MAX_LENGTH) {
        e.preventDefault()
        e.stopPropagation()
        this.colorwaysTagError =
          'Colorway codes must be exactly 3 letters (A–Z)'

        return
      }

      this.colorwaysTagError = null
    },

    /**
     * @param {string} upper
     */
    highlightColorwaysDuplicate (upper) {
      this.$nextTick().then(() => {
        const tagEls =
          this.$refs.colorwaysSelectRef?.$el.querySelectorAll('.el-tag')
        tagEls &&
          tagEls.forEach(tagEl => {
            const label = tagEl
              .querySelector('.el-select__tags-text')
              ?.textContent?.trim()

            if (label === upper) {
              tagEl.classList.add('is-duplicate')
              setTimeout(() => tagEl.classList.remove('is-duplicate'), 1500)
            }
          })
      })
    },

    /**
     *
     */
    async saveResponsibleProduct () {
      if (this.editableResponsibleProduct.id) {
        await this.updateResponsibleProduct({
          styleId: this.item.id,
          responsibleProductId: this.editableResponsibleProduct.id
        })

        this.editableResponsibleProduct = {
          id: null,
          email: null,
          edit: false
        }
      }
    },

    /**
     *
     */
    async resetResponsibleProduct () {
      await this.updateResponsibleProduct({
        styleId: this.item.id,
        responsibleProductId: null
      })

      this.editableResponsibleProduct = {
        id: null,
        email: null,
        edit: false
      }
    },

    /**
     *
     */
    async resetDigitalApprover () {
      await this.updateDigitalApprover({
        styleId: this.item.id,
        digitalApproverId: null
      })

      this.editableDigitalApprover = {
        id: null,
        email: null,
        edit: false
      }
    },

    /**
     *
     */
    async saveDigitalApprover () {
      if (this.editableDigitalApprover.id) {
        await this.updateDigitalApprover({
          styleId: this.item.id,
          digitalApproverId: this.editableDigitalApprover.id
        })

        this.editableDigitalApprover = {
          id: null,
          email: null,
          edit: false
        }
      }
    },

    /**
     *
     */
    cancelEditingResponsibleProduct () {
      this.editableResponsibleProduct.edit =
        !this.editableResponsibleProduct.edit
    },

    /**
     *
     */
    cancelEditingDigitalApprover () {
      this.editableDigitalApprover.edit = !this.editableDigitalApprover.edit
    },

    /**
     *
     */
    async saveSubmissionDeadline () {
      if (this.editableSubmissionDeadline.deadline) {
        await this.updateSubmissionDeadline({
          styleId: this.item.id,
          deadline: this.editableSubmissionDeadline.deadline
        })

        this.editableSubmissionDeadline = {
          deadline: null,
          edit: false
        }
      }
    },

    /**
     *
     */
    cancelEditingSubmissionDeadline () {
      this.editableSubmissionDeadline.edit =
        !this.editableSubmissionDeadline.edit
    },

    /**
     */
    startEditingColorwaysRequested () {
      this.editableColorwaysRequested = {
        colorwaysRequested: Array.isArray(this.localColorwaysRequested)
          ? [...this.localColorwaysRequested]
          : [],
        edit: true
      }
    },

    /**
     */
    cancelEditingColorwaysRequested () {
      this.editableColorwaysRequested = {
        colorwaysRequested: [],
        edit: false
      }
    },

    /**
     */
    async saveColorwaysRequested () {
      if (this.colorwaysTagError) {
        return
      }

      this.isLoadingColorwaysRequested = true

      const colorwaysRequested = [
        ...this.editableColorwaysRequested.colorwaysRequested
      ]

      try {
        await this.updateItem({
          itemId: this.item.id,
          libraryType: this.libraryType,
          new: {
            groupId: this.item.groupId,
            seasonId: this.item.seasonId,
            name: this.item.name,
            colorwaysRequested
          }
        })

        this.localColorwaysRequested = colorwaysRequested

        this.editableColorwaysRequested = {
          colorwaysRequested: [],
          edit: false
        }
      } finally {
        this.isLoadingColorwaysRequested = false
      }
    },

    /**
     * @param {string} status
     */
    async updateStatusWithoutConfirmation (status) {
      this.isLoadingStatus = true

      await this.updateAssetStatus({
        id: this.item.id,
        assetStatus: status,
        isCritical: false
      })

      this.isLoadingStatus = false
    },

    /**
     * @param {number} vendorId
     */
    async updateStyleVendor (vendorId) {
      this.isLoadingVendor = true

      await this.updateVendor({
        libraryType: this.libraryType,
        itemId: this.item.id,
        vendorId: vendorId
      })

      this.isLoadingVendor = false
    },

    /**
     * @param {boolean} value
     */
    async onQualityCheckChange (value) {
      this.isLoadingQualityCheck = true

      if (this.styleQualityCheckStatus === null) {
        this.styleQualityCheckStatus = true
      } else {
        this.styleQualityCheckStatus = value
      }

      await this.updateQualityCheckStatus({
        styleId: this.item.id,
        status: this.styleQualityCheckStatus
      })

      this.isLoadingQualityCheck = false
    },

    /**
     */
    async onQualityCheckReset () {
      this.isLoadingQualityCheck = true

      this.styleQualityCheckStatus = null

      await this.updateQualityCheckStatus({
        styleId: this.item.id,
        status: null
      })

      this.isLoadingQualityCheck = false
    },

    /**
     */
    async copyToClipboard () {
      try {
        await navigator.clipboard.writeText(this.item.plmCode)
      } catch (error) {
        console.error(
          `Something went wrong while copying the PLM Code: ${error.message}`
        )
      }
    }
  }
}
</script>

<style lang="scss">
.colorways-select__hidden-dropdown {
  display: none !important;
}
</style>

<style lang="scss" scoped>
$meta-list-name-width: 50%;
$meta-list-value-width: 50%;

.detail-attributes {
  display: flex;
  width: 100%;
  height: 100vh;
  margin-bottom: 0;
  padding: spacing(2);
  padding-bottom: spacing(25);
}

.detail-attributes__info {
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
}

.detail-attributes__button-actions {
  padding-left: spacing(1);
  font-size: spacing(1.8);
  cursor: pointer;
  transition: $transition;
}

.detail-attributes__button-actions:hover {
  font-size: spacing(2);
}

.detail-attributes__table {
  width: 100%;
  margin-bottom: spacing(1);

  tr {
    display: flex;
    margin-bottom: spacing(2);
  }

  td {
    padding: spacing(1) 0;
  }

  td + td {
    margin-left: spacing(1);
  }

  td:first-of-type {
    width: $meta-list-name-width;
    color: $grey;
    font-weight: $font-weight-bold;
  }

  td:last-of-type {
    width: $meta-list-value-width;
    color: $grey-light;
  }
}

.detail-attributes__wide-value {
  td:first-of-type {
    width: percentage(40);
  }

  td:last-of-type {
    width: percentage(60);
  }
}

.detail-attributes__info-line {
  margin-bottom: spacing(3);
  margin-left: spacing(0.5);

  .title {
    color: $grey;
    font-weight: $font-weight-bold;
  }

  .value {
    color: $grey-light;
  }
}

.detail-attributes__qc-reset {
  width: spacing(3);
  padding: 0;
}

.close {
  color: $orange !important;
}

.passed {
  color: $red !important;
}

.detail-attributes__info-line .el-date-editor.el-input,
.detail-attributes__info-line .el-select {
  width: percentage(70);
}

.detail-attributes__copy-button {
  padding: 0;

  i {
    margin-left: spacing(0.5);
    color: $blue;
    font-size: spacing(2.2);
    cursor: pointer;
  }
}

.detail-attributes__colorway-tag {
  display: inline-flex;
  align-items: center;
  margin: spacing(0.25) spacing(0.5) spacing(0.25) 0;
  padding: spacing(0.45) spacing(1);
  color: $grey;
  font-weight: $font-weight-bold;
  font-size: spacing(1.5);
  line-height: spacing(2.2);
  letter-spacing: 0.03em;
  text-transform: uppercase;
  background-color: $grey-very-light;
  border: 1px solid rgba($grey-light, 0.25);
  border-radius: spacing(0.75);
}

.detail-attributes__colorways-value {
  display: block;
}

.detail-attributes__colorways-select-wrapper {
  display: block;
}

.detail-attributes__colorways-select {
  display: block;
  width: 100% !important;
}

.detail-attributes__colorways-select.has-error {
  /deep/ .el-input__inner {
    border-color: $red;

    &:focus {
      border-color: $red;
    }
  }
}

.detail-attributes__colorways-error {
  display: block;
  padding-top: spacing(0.5);
  color: $red;
  font-size: spacing(1.5);
}

/deep/ .el-tag.is-duplicate {
  color: $green;
  background-color: $green-light;
  border-color: $green;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;

  .el-tag__close {
    color: $green;

    &:hover {
      color: $white;
      background-color: $green;
    }
  }
}
</style>

<style lang="scss">
.detail-attributes__qc-neutral-state .el-switch__core {
  background-color: $grey-very-light !important;
  border-color: $grey-very-light !important;
}
</style>
